import {
  Box,
  Button,
  Grid,
  Paper,
  Divider,
  debounce,
  TextField,
  InputAdornment, alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { TbBuildingEstate } from "react-icons/tb";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { WSIcons } from "../../commonFunctions/iIcons";

import WSTextField from '../../Components/TextBox/WSTextBox';
import SectionHeader from '../components/header'

import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import DataTable from "../components/dataTable";
import { Column } from '../components/dataTable'
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import ConfirmationPopup from "../../Components/Confirmation/Confirmation";

export type District = {
  _id?: string;
  name: string;
  code: string;
};

const DistrictManager = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [districts, setDistricts] = useState<District[]>([]);
  const [editId, setEditId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5)
  const [isPopupOpen, setPopupOpen] = useState(false); //for delete;
  const [selectedIdForDlt, setSelectedIdForDlt] =
    useState<any>(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [sortOption, setSortOption] = useState("created_desc");


  const { control, handleSubmit, reset, setValue } = useForm<District>();
  const { showMessage } = useSnackbar();


  const fetchData = useCallback(
    debounce(async (pageVal: number, searchVal: string, limitVal: number, statusVal: string, sortVal: string) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          page: String(pageVal),
          search: searchVal,
          limit: String(limitVal),
          ...(statusVal && { status: statusVal }),
          ...(sortVal && { sort: sortVal }),
        });

        const res = await callAPI(
          `districts/getPaginated?${query.toString()}`,
          Method.GET
        );

        if (res.success) {
          setDistricts(res.districts || []);
          setTotalPages(res.totalPages || 0);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    }, 400),
    [setLoading, setDistricts, setTotalPages]
  );

  const onSubmit = async (data: District) => {
    try {
      setSubmitting(true);
      if (editId) {
        await callAPI(`districts/update/${editId}`, Method.PUT, data);
        showMessage("District Updated Successfully", "success");
      } else {
        await callAPI("districts/create", Method.POST, data);
        showMessage("District Created Successfully", "success");
      }

      reset();
      setEditId(null);
      fetchData(page, search, rowsPerPage, statusFilter, sortOption);
    } catch (error: any) {
      if (error.message === 'GlobalHandledError') return;
      console.error("Error while submitting district:", error);
      showMessage(error?.response?.data?.message || "An unexpected error occurred", "error");
    } finally {
      setSubmitting(false);
    }
  };


  const handleEdit = (district: District) => {
    setValue("name", district.name);
    setValue("code", district.code);
    setEditId(district._id || null);
  };

  const handleDelete = () => {
    showMessage("District Deleted Successfully", "success");
    fetchData(page, search, rowsPerPage, statusFilter, sortOption);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  useEffect(() => {
    fetchData(page, search, rowsPerPage, statusFilter, sortOption);
  }, [page, search, rowsPerPage, statusFilter, sortOption]);


  const confirmDelete = (data: any) => {
    setSelectedIdForDlt(data._id);
    setPopupOpen(true)
  };

  const columns: Column[] = [
    { field: "code", label: "Code" },
    { field: "name", label: "Name" }
    // {
    //   field: "createdAt",
    //   label: "Created",
    //   sortable: true,
    //   type: "date",
    // },
  ];


  const actions = [
    { label: "Edit", handler: handleEdit },
    { label: "Delete", handler: confirmDelete },
  ]

  const handleCancel = () => {
    setEditId(null);
    reset();
  }

  return (
    <Box p={3} mb={6}>
      <SectionHeader title={"District Management"} icon={<TbBuildingEstate fontSize={"large"} />} subtitle={editId ? "✏️ Update District" : "➕ Add New District"} />
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderTopLeftRadius: 0, borderTopRightRadius: 0, borderBottomRightRadius: 3, borderBottomLeftRadius: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <WSTextField
                id="district"
                label="District Name *"
                name="name"
                control={control}
                rules={{ required: "District name is required" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <WSTextField
                id="code"
                label="District Code *"
                name="code"
                control={control}
                rules={{ required: "District code is required" }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="medium"
                sx={{
                  background: editId ? "#1976d2" : "#4DD26B",
                  color: "white",
                  textTransform: "capitalize",
                  px: 4,
                  fontWeight: "bold",
                  "&:hover": {
                    background: editId ? "#1565c0" : "#4DD26B",
                  },
                }}
                disabled={submitting}
              >
                {editId ? "Update District" : "Add District"}
              </Button>
              {editId && (
                <Button size="medium" onClick={handleCancel} sx={{ ml: 2 }} color="secondary">
                  Cancel
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Box
        display="flex" gap={2} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}
      >
        {/* 🔍 Search */}
        <TextField
          label="Search Districts..."
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          fullWidth
          sx={{
            flex: 1,
            backgroundColor: (theme) => alpha(theme.palette.grey[50], 0.9),
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '& fieldset': {
                borderColor: '#ddd',
              },
              '&:hover fieldset': {
                borderColor: '#bbb',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#4A00E0',
                borderWidth: 2,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* 🎯 Status Filter */}
        {/* <FormControl
    size="small"
    sx={{
      minWidth: 180,
      backgroundColor: '#f9f9fb',
      borderRadius: 1,
      boxShadow: 0,
    }}
  >
    <InputLabel id="status-filter-label">
      <WSIcons.Filter sx={{ mr: 0.5, fontSize: 18 }} /> Status
    </InputLabel>
    <Select
      labelId="status-filter-label"
      label="Status"
      value={statusFilter}
      onChange={(e) => {
        setStatusFilter(e.target.value);
        setPage(1);
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: 1,
            boxShadow: 1,
          },
        },
      }}
    >
      <MenuItem value="">All</MenuItem>
      <MenuItem value="active">🟢 Active</MenuItem>
      <MenuItem value="inactive">🔴 Inactive</MenuItem>
    </Select>
  </FormControl> */}

        {/* ⬇️ Sort By */}
        <FormControl
          size="small"
          sx={{
            minWidth: 200,
            backgroundColor: '#f9f9fb',
            borderRadius: 1,
            boxShadow: 0,
          }}
        >
          <InputLabel id="sort-option-label">
            <WSIcons.Sort sx={{ mr: 0.5, fontSize: 18 }} /> Sort By
          </InputLabel>
          <Select
            labelId="sort-option-label"
            label="Sort By"
            value={sortOption}
            onChange={(e) => {
              setSortOption(e.target.value);
              setPage(1);
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 1,
                  boxShadow: 1,
                },
              },
            }}
          >
            <MenuItem value="name_asc">🔤 Name (A → Z)</MenuItem>
            <MenuItem value="name_desc">🔡 Name (Z → A)</MenuItem>
            <MenuItem value="created_desc">🆕 Newest</MenuItem>
            <MenuItem value="created_asc">📜 Oldest</MenuItem>
          </Select>
        </FormControl>
      </Box>



      <DataTable columns={columns} data={districts}
        loading={loading} actions={actions} onPageChange={setPage}
        rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
        page={page} totalPages={totalPages} />



      {isPopupOpen && (
        <ConfirmationPopup
          apiUrl={"districts/delete"}
          id={selectedIdForDlt}
          confirmMessage="Are you sure you want to delete this distict?"
          open={isPopupOpen}
          onClose={() => setPopupOpen(false)}
          onConfirm={handleDelete}
          permanent
        />
      )}
    </Box>
  );
};

export default DistrictManager;
